/* Owns the Topbar server dropdown of PANEL.md §4: a searchable list over
   `UserDto.servers` from `GET /v1/auth/me` — status dot, name, players online,
   role chip — with `server.new` in the footer. It is a listbox, not a menu: the
   options are a set of values one of which is current, which is what
   `aria-selected` and roving focus describe. */

import type { MeServerSummary } from '@sentinelac/shared'
import { ChevronsUpDown, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { Badge } from '../components/ui/Badge.js'
import { cn } from '../components/ui/cn.js'
import { useT } from '../i18n/index.js'
import { useAuthState } from '../lib/auth-store.js'
import { roleLabelKey, SERVER_STATUS_CLASSES, serverStatusLabelKey } from '../lib/format.js'

interface StatusDotProps {
  status: MeServerSummary['status']
  label: string
}

/** Colour is never the only channel: the textual status always rides along as a label. */
function StatusDot({ status, label }: StatusDotProps): ReactNode {
  return <span aria-label={label} className={cn('size-2 shrink-0 rounded-full', SERVER_STATUS_CLASSES[status].bg)} />
}

export function ServerSwitcher(): ReactNode {
  const t = useT()
  const navigate = useNavigate()
  const { servers, activeServerId } = useAuthState()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  const current = servers.find((server) => server.id === activeServerId) ?? null

  const matches = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase()
    if (!needle) return servers
    return servers.filter((server) => server.name.toLocaleLowerCase().includes(needle))
  }, [servers, query])

  useEffect(() => {
    if (!open) return
    setActiveIndex(0)
    searchRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const choose = (server: MeServerSummary): void => {
    setOpen(false)
    setQuery('')
    void navigate(`/s/${server.id}/dashboard`)
  }

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (matches.length === 0 ? 0 : (index + 1) % matches.length))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (matches.length === 0 ? 0 : (index - 1 + matches.length) % matches.length))
      return
    }
    if (event.key === 'Enter') {
      const server = matches[activeIndex]
      if (server) {
        event.preventDefault()
        choose(server)
      }
    }
  }

  const listboxId = 'server-switcher-listbox'

  return (
    <div ref={containerRef} className="relative" onKeyDown={onKeyDown}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => setOpen((value) => !value)}
        data-testid="server-switcher"
        className="flex min-h-11 max-w-64 items-center gap-2 rounded-md border border-border-strong bg-panel px-3 text-sm text-text hover:bg-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:min-h-9"
      >
        {current ? <StatusDot status={current.status} label={t(serverStatusLabelKey(current.status))} /> : null}
        <span className="truncate">{current ? current.name : t('server.switcher')}</span>
        <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-text-faint" />
      </button>

      {open ? (
        <div className="absolute z-40 mt-1 w-80 max-w-[92vw] rounded-lg border border-border-strong bg-elevated shadow-pop">
          <div className="border-b border-border p-2">
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('common.searchPlaceholder')}
              aria-label={t('common.search')}
              aria-controls={listboxId}
              className="w-full rounded-md border border-border bg-panel px-2 py-1.5 text-sm text-text placeholder:text-text-faint focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-accent"
            />
          </div>
          <ul id={listboxId} role="listbox" aria-label={t('server.switcher')} className="max-h-72 overflow-y-auto p-1">
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-sm text-text-faint">{t('common.noResults')}</li>
            ) : (
              matches.map((server, index) => (
                <li key={server.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={server.id === activeServerId}
                    tabIndex={-1}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(server)}
                    className={cn(
                      'flex w-full min-h-11 items-center gap-2 rounded-md px-2 text-left text-sm md:min-h-9',
                      index === activeIndex ? 'bg-panel text-text' : 'text-text-muted hover:bg-panel',
                    )}
                  >
                    <StatusDot status={server.status} label={t(serverStatusLabelKey(server.status))} />
                    <span className="flex-1 truncate text-text">{server.name}</span>
                    <span className="tabular-nums text-xs text-text-faint">{server.playersOnline}</span>
                    <Badge tone="neutral" variant="outline" size="sm">
                      {t(roleLabelKey(server.role))}
                    </Badge>
                  </button>
                </li>
              ))
            )}
          </ul>
          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                void navigate('/servers/new')
              }}
              className="flex w-full min-h-11 items-center gap-2 rounded-md px-2 text-left text-sm text-text-muted hover:bg-panel hover:text-text md:min-h-9"
            >
              <Plus aria-hidden className="size-4" />
              {t('server.new')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
